import React, { useCallback } from 'react'
import { Sparkles } from 'lucide-react'
import { CardContent } from '@/components/ui/card'
import { NodeContainer } from '../base/NodeContainer'
import { NODE_DIMENSIONS } from '@genieartinc/schemas'

interface LoraModelProps {
  id: string
  selected?: boolean
  onHoverChange?: (hovered: boolean) => void
  onSelect?: () => void
  onStateChange?: (
    updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>),
  ) => void
  state?: Record<string, unknown>
}

function LoraModelComponent({ id, selected = false, onHoverChange, onSelect, onStateChange, state = {} }: LoraModelProps) {
  const name = (state.title as string | undefined) || (state.name as string | undefined) || 'LoRA'
  const triggerWord = state.triggerWord as string | null | undefined
  const thumbnailUrl = state.thumbnailUrl as string | null | undefined
  const scale = typeof state.scale === 'number' ? (state.scale as number) : 1

  const handleScaleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = Number(e.target.value)
      if (!Number.isFinite(next)) return
      const clamped = Math.max(0, Math.min(2, next))
      onStateChange?.((prev) => ({ ...prev, scale: clamped }))
    },
    [onStateChange],
  )

  return (
    <NodeContainer
      nodeId={id}
      title={name}
      icon="sparkles"
      selected={selected}
      nodeType="image"
      width={NODE_DIMENSIONS.LoraModel.width}
      onSelect={onSelect}
      onHoverChange={onHoverChange}
      onStateChange={onStateChange}
      state={state}
    >
      <CardContent className="flex flex-col gap-2 p-2">
        {/* Preview image — square aspect fills card width */}
        <div className="relative aspect-square w-full overflow-hidden rounded-[10px] bg-muted">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={name}
              className="h-full w-full object-cover"
              draggable={false}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Sparkles className="h-8 w-8 text-muted-foreground/40" />
            </div>
          )}
          {triggerWord && (
            <div className="absolute left-1.5 top-1.5 rounded-md bg-background/80 px-1.5 py-0.5 font-mono text-[11px] text-foreground backdrop-blur-sm">
              {triggerWord}
            </div>
          )}
        </div>

        {/* Strength slider — compact footer */}
        <div className="flex items-center gap-2 px-0.5">
          <span className="text-[11px] text-muted-foreground">Strength</span>
          <input
            type="range"
            min={0}
            max={2}
            step={0.05}
            value={scale}
            onChange={handleScaleChange}
            className="nodrag h-1.5 flex-1 cursor-pointer appearance-none rounded bg-muted accent-foreground"
            onMouseDown={(e) => e.stopPropagation()}
          />
          <span className="w-9 text-right font-mono text-[11px] tabular-nums text-foreground">{scale.toFixed(2)}</span>
        </div>
      </CardContent>
    </NodeContainer>
  )
}

export const LoraModel = React.memo(LoraModelComponent)
LoraModel.displayName = 'LoraModel'
